import React from "react"
import { useSelector, useDispatch } from "react-redux"

import {
    QidModelFailureMode,
    QidModelOption,
} from "server/models"
import {
    StoreType,
    initQidModelOption,
    initQidModelFailureMode,

    setQidModelDeleteOption,
    setQidModelDeleteFailureMode,
    setQidModelExpandedModelIds,
    setQidModelExpandedSafetyFunctionIds,
} from "client/store"
import {
    RowSizeType,
    TableRow,
    TableColumn,
    TableCell,
    TableCellContentWrapper,
    TableCellContent,

    Empty,
    Label,
    Link,
    CrossIcon,
} from "client/components"

import {
    ContentColumn,
    ContentItem,
    DeleteContentItemButton,
    ContentButton,
    TableCellLined,
    RowCollapseControl,
} from "./styled"

import {
    openSafetyFunction,
    openOption,
    openFailureMode,
} from "./open"

const rowSize: keyof RowSizeType = "sm"

const Rows = (
    productEditAccess: boolean,
    columns: TableColumn[],
) => {
    const modelList = useSelector((state: StoreType) => state.qidModel.modelList)
    const modelIds = useSelector((state: StoreType) => state.qidModel.modelIds)

    const expandedModelIds = useSelector((state: StoreType) => state.qidModel.expandedModelIds)
    const expandedSafetyFunctionIds = useSelector((state: StoreType) => state.qidModel.expandedSafetyFunctionIds)

    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const toggleModel = (modelId: number) => {
        const expandedModels = expandedModelIds.slice()
        if (expandedModels.includes(modelId)) {
            dispatch(setQidModelExpandedModelIds(expandedModels.filter((id) => id !== modelId)))
        } else {
            expandedModels.push(modelId)
            dispatch(setQidModelExpandedModelIds(expandedModels))
        }
    }

    const toggleSafetyFunction = (functionId: number) => {
        const expandedFunctions = expandedSafetyFunctionIds.slice()
        if (expandedFunctions.includes(functionId)) {
            dispatch(setQidModelExpandedSafetyFunctionIds(expandedFunctions.filter((id) => id !== functionId)))
        } else {
            expandedFunctions.push(functionId)
            dispatch(setQidModelExpandedSafetyFunctionIds(expandedFunctions))
        }
    }

    const deleteOption = (option: QidModelOption) => {
        dispatch(setQidModelDeleteOption(option))
    }

    const deleteFailureMode = (failureMode: QidModelFailureMode) => {
        dispatch(setQidModelDeleteFailureMode(failureMode))
    }

    const rows: React.ReactNode[] = []

    const modelColumn = columns[0]
    const safetyFunctionColumn = columns[1]
    const propertyColumns = columns.slice(2)

    modelIds.forEach((modelId) => {
        const model = modelList[modelId]
        if (!model) return

        const modelExpanded = expandedModelIds.includes(modelId)
        rows.push(
            <TableRow
                key={`model_${model.id}`}
                size={rowSize}
                onClick={() => toggleModel(model.id)}
            >
                <TableCell
                    colSpan={2} // model + safety function columns
                    cellWidth={modelColumn.cellWidth || null}
                    cellMinWidth={modelColumn.cellMinWidth || null}
                    cellMaxWidth={modelColumn.cellMaxWidth || null}
                    centerAlign={modelColumn.centerAlign}
                >
                    <TableCellContentWrapper style={modelColumn.style}>
                        <TableCellContent>
                            <RowCollapseControl collapsed={!modelExpanded} />
                            {modelColumn.formatter(model)}
                        </TableCellContent>
                    </TableCellContentWrapper>
                </TableCell>
                {
                    propertyColumns.map((column) => (
                        <TableCell
                            key={column.field}
                            cellWidth={column.cellWidth || null}
                            cellMinWidth={column.cellMinWidth || null}
                            cellMaxWidth={column.cellMaxWidth || null}
                            centerAlign={column.centerAlign}
                        >
                            <TableCellContentWrapper>
                                <TableCellContent>
                                    {column.formatter(model)}
                                </TableCellContent>
                            </TableCellContentWrapper>
                        </TableCell>
                    ))
                }
            </TableRow>,
        )

        if (modelExpanded) {
            const { safetyFunctions } = model

            if (!safetyFunctions.length) {
                rows.push(
                    <TableRow key={`emptyModel_${model.id}`} size={rowSize}>
                        <TableCellLined
                            cellWidth={modelColumn.cellWidth || null}
                            cellMinWidth={modelColumn.cellMinWidth || null}
                            cellMaxWidth={modelColumn.cellMaxWidth || null}
                            lineThrough={productEditAccess} // show "tree" line only if "Add safety function" button is shown
                        />
                        <TableCell colSpan={100}>
                            <TableCellContentWrapper>
                                <Empty />
                            </TableCellContentWrapper>
                        </TableCell>
                    </TableRow>,
                )
            }
            safetyFunctions.forEach((safetyFunction, index) => {
                const functionExpanded = expandedSafetyFunctionIds.includes(safetyFunction.id)
                const lastRow = safetyFunctions.length - 1 === index
                rows.push(
                    <TableRow
                        key={`safetyFunction_${safetyFunction.id}`}
                        size={rowSize}
                        onClick={() => toggleSafetyFunction(safetyFunction.id)}
                    >
                        <TableCellLined
                            cellWidth={modelColumn.cellWidth || null}
                            cellMinWidth={modelColumn.cellMinWidth || null}
                            cellMaxWidth={modelColumn.cellMaxWidth || null}
                            // all rows have "cross line"
                            // but if no edit access the last row has only "half line"
                            lineCross={!lastRow || (productEditAccess && lastRow)}
                            lineHalfCross={!productEditAccess && lastRow}
                        />
                        <TableCell
                            cellWidth={safetyFunctionColumn.cellWidth || null}
                            cellMinWidth={safetyFunctionColumn.cellMinWidth || null}
                            cellMaxWidth={safetyFunctionColumn.cellMaxWidth || null}
                            centerAlign={safetyFunctionColumn.centerAlign}
                        >
                            <TableCellContentWrapper style={safetyFunctionColumn.style}>
                                <TableCellContent>
                                    <RowCollapseControl collapsed={!functionExpanded} />
                                    {safetyFunctionColumn.formatter(safetyFunction)}
                                </TableCellContent>
                            </TableCellContentWrapper>
                        </TableCell>
                        {
                            propertyColumns.map((column) => (
                                <TableCell
                                    key={column.field}
                                    cellWidth={column.cellWidth || null}
                                    cellMinWidth={column.cellMinWidth || null}
                                    cellMaxWidth={column.cellMaxWidth || null}
                                    centerAlign={column.centerAlign}
                                >
                                    <TableCellContentWrapper>
                                        <TableCellContent>
                                            {column.formatter(safetyFunction)}
                                        </TableCellContent>
                                    </TableCellContentWrapper>
                                </TableCell>
                            ))
                        }
                    </TableRow>,
                )

                if (functionExpanded) {
                    rows.push(
                        <TableRow
                            key={`safetyFunctionContent_${safetyFunction.id}`}
                            size="fit"
                        >
                            <TableCellLined
                                cellWidth={modelColumn.cellWidth || null}
                                cellMinWidth={modelColumn.cellMinWidth || null}
                                cellMaxWidth={modelColumn.cellMaxWidth || null}
                                lineThrough={!lastRow || (productEditAccess && lastRow)}
                            />
                            <TableCell
                                cellWidth={safetyFunctionColumn.cellWidth || null}
                                cellMinWidth={safetyFunctionColumn.cellMinWidth || null}
                                cellMaxWidth={safetyFunctionColumn.cellMaxWidth || null}
                            />
                            <TableCell colSpan={100}>
                                <TableCellContentWrapper style={{ alignItems: "start" }}>
                                    <ContentColumn>
                                        <Label>{lang.options}</Label>
                                        {
                                            !safetyFunction.options.length
                                            && <Empty />
                                        }
                                        {
                                            safetyFunction.options.map((option) => (
                                                <ContentItem key={option.name}>
                                                    {
                                                        productEditAccess
                                                            ? (
                                                                <Link
                                                                    href="/#"
                                                                    onClick={() => openOption(
                                                                        safetyFunction.id,
                                                                        option,
                                                                        dispatch,
                                                                    )}
                                                                >
                                                                    {option.name}
                                                                </Link>
                                                            )
                                                            : option.name
                                                    }
                                                    {
                                                        productEditAccess && (
                                                            <DeleteContentItemButton onClick={() => deleteOption(option)}>
                                                                <CrossIcon />
                                                            </DeleteContentItemButton>
                                                        )
                                                    }
                                                </ContentItem>
                                            ))
                                        }
                                        {
                                            productEditAccess && (
                                                <ContentButton
                                                    fullWidth
                                                    marginTop
                                                    variant="success"
                                                    onClick={() => openOption(
                                                        safetyFunction.id,
                                                        initQidModelOption,
                                                        dispatch,
                                                    )}
                                                >
                                                    {lang.addOption}
                                                </ContentButton>
                                            )
                                        }
                                    </ContentColumn>
                                    <ContentColumn>
                                        <Label>{lang.failureModes}</Label>
                                        {
                                            !safetyFunction.failureModes.length
                                            && <Empty />
                                        }
                                        {
                                            safetyFunction.failureModes.map((failureMode) => (
                                                <ContentItem key={failureMode.name}>
                                                    {
                                                        productEditAccess
                                                            ? (
                                                                <Link
                                                                    href="/#"
                                                                    onClick={() => openFailureMode(
                                                                        safetyFunction.id,
                                                                        failureMode,
                                                                        dispatch,
                                                                    )}
                                                                >
                                                                    {failureMode.name}
                                                                </Link>
                                                            )
                                                            : failureMode.name
                                                    }
                                                    {
                                                        productEditAccess && (
                                                            <DeleteContentItemButton onClick={() => deleteFailureMode(failureMode)}>
                                                                <CrossIcon />
                                                            </DeleteContentItemButton>
                                                        )
                                                    }
                                                </ContentItem>
                                            ))
                                        }
                                        {
                                            productEditAccess && (
                                                <ContentButton
                                                    fullWidth
                                                    marginTop
                                                    variant="success"
                                                    onClick={() => openFailureMode(
                                                        safetyFunction.id,
                                                        initQidModelFailureMode,
                                                        dispatch,
                                                    )}
                                                >
                                                    {lang.addFailureMode}
                                                </ContentButton>
                                            )
                                        }
                                    </ContentColumn>
                                </TableCellContentWrapper>
                            </TableCell>
                        </TableRow>,
                    )
                }
            })

            if (productEditAccess) {
                rows.push(
                    <TableRow
                        key={`addSafetyFunction_${model.id}`}
                        size="fit"
                    >
                        <TableCellLined
                            cellWidth={modelColumn.cellWidth || null}
                            cellMinWidth={modelColumn.cellMinWidth || null}
                            cellMaxWidth={modelColumn.cellMaxWidth || null}
                            lineHalfCross
                        />
                        <TableCell colSpan={100}>
                            <TableCellContentWrapper>
                                <TableCellContent>
                                    <ContentButton variant="success" onClick={() => openSafetyFunction(model.id, 0, dispatch)}>
                                        {lang.addSafetyFunction}
                                    </ContentButton>
                                </TableCellContent>
                            </TableCellContentWrapper>
                        </TableCell>
                    </TableRow>,
                )
            }
        }
    })

    return rows
}
export default Rows
